import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaChevronRight,
    FaSearch,
    FaEye
} from 'react-icons/fa';

const EngineerInterGovernanceWorksList = () => {
    const navigate = useNavigate();
    const [department, setDepartment] = useState('');
    const [taluka, setTaluka] = useState('');
    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [appliedFilters, setAppliedFilters] = useState({ dept: '', taluka: '' });

    const talukaList = [
        { en: 'Shirala', mr: 'शिराळा' },
        { en: 'Walwa', mr: 'वाळवा' },
        { en: 'Tasgaon', mr: 'तासगाव' },
        { en: 'Khanapur', mr: 'खानापूर (विटा)' },
        { en: 'Atpadi', mr: 'आटपाडी' },
        { en: 'Kavathe Mahankal', mr: 'कवठे महांकाळ' },
        { en: 'Miraj', mr: 'मिरज' },
        { en: 'Palus', mr: 'पलूस' },
        { en: 'Jat', mr: 'जत' },
        { en: 'Kadegaon', mr: 'कडेगाव' },
        { en: 'Mukhyalaya', mr: 'मुख्यालय' }
    ];

    const talukas = [
        "मिरज", "जत", "खानापूर (विटा)", "वाळवा (इस्लामपूर)", "तासगाव", 
        "शिराळा", "कडेगाव", "पलूस", "कवठे महांकाळ"
    ];

    const departments = ["समाज", "सु. बे."];

    // Marathi numerals for Sr.No.
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);

        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        const unsubWorks = onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const datesData = snapshot.val();
                let allFlattenedWorks = [];
                Object.entries(datesData).forEach(([dateStr, dateSnapshot]) => {
                    const traverseNode = (node) => {
                        if (!node || typeof node !== 'object') return;
                        if (node.schemeName || node.workName) {
                            allFlattenedWorks.push({
                                ...node,
                                id: node.id || node.workNumber || Math.random().toString(36).substr(2, 9),
                                meetingNumber: dateStr
                            });
                        } else {
                            Object.values(node).forEach(child => traverseNode(child));
                        }
                    };
                    traverseNode(dateSnapshot);
                });
                setWorks(allFlattenedWorks);
            }
        });

        // Fetch Registrations for interest calculation fallback
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkDone = () => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                ];
                setRegistrations(allRegs);
                setLoading(false);
            }
        };

        const unsubEng = onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; checkDone(); });
        const unsubSoc = onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; checkDone(); });

        return () => {
            unsubWorks();
            unsubEng();
            unsubSoc();
        };
    }, []);

    const normalizeTaluka = (name) => {
        if (!name) return "";
        const n = name.toString().toLowerCase().trim();
        const standardMap = {
            'shirala': 'shirala', 'शिराळा': 'shirala',
            'walwa': 'walwa', 'वाळवा': 'walwa', 'islampur': 'walwa',
            'tasgaon': 'tasgaon', 'तासगाव': 'tasgaon',
            'khanapur': 'khanapur', 'खानापूर': 'khanapur', 'vita': 'khanapur', 'खानापूर (विटा)': 'khanapur',
            'atpadi': 'atpadi', 'आटपाडी': 'atpadi',
            'kavathe mahankal': 'kavathe mahankal', 'कवठे महांकाळ': 'kavathe mahankal', 'kavathemahangal': 'kavathe mahankal', 'कवठेमहांकाळ': 'kavathe mahankal',
            'miraj': 'miraj', 'मिरज': 'miraj', 'sangli': 'miraj',
            'palus': 'palus', 'पलूस': 'palus',
            'jat': 'jat', 'जत': 'jat', 'jath': 'jat',
            'kadegaon': 'kadegaon', 'कडेगाव': 'kadegaon',
            'mukhyalaya': 'mukhyalaya', 'मुख्यालय': 'mukhyalaya'
        };
        return standardMap[n] || n;
    };

    const getMarathiTaluka = (enName) => {
        if (!enName) return "-";
        const normalizedEn = normalizeTaluka(enName);
        const found = talukaList.find(t => normalizeTaluka(t.en) === normalizedEn);
        return found ? found.mr : enName;
    };

    const handleSearch = () => {
        setAppliedFilters({ dept: department, taluka: taluka });
    };

    const filteredWorks = useMemo(() => {
        return works.filter(work => {
            const isSociety = appliedFilters.dept === 'समाज';
            const isWorkSociety = (work.category || '').toLowerCase().includes('society') || 
                                 (work.registrationType || '').toLowerCase().includes('society') ||
                                 (work.sharedwork || '').includes('मजूर');
            const deptMatch = !appliedFilters.dept || (isSociety === isWorkSociety);
            
            const standardizedApplied = normalizeTaluka(appliedFilters.taluka);
            const standardizedWork = normalizeTaluka(work.taluka);
            const talukaMatch = !appliedFilters.taluka || standardizedWork === standardizedApplied;
            
            return deptMatch && talukaMatch;
        });
    }, [works, appliedFilters]);

    const workInterestMap = useMemo(() => {
        const counts = {};
        registrations.forEach(reg => {
            if (reg.work_payment) {
                Object.entries(reg.work_payment).forEach(([meetingNum, paymentData]) => {
                    const selected = paymentData.selectedWorks || [];
                    selected.forEach(workStr => {
                        const workNum = workStr.split(' - ')[0];
                        const key = `${meetingNum}_${workNum}`;
                        counts[key] = (counts[key] || 0) + 1;
                    });
                });
            }
        });
        return counts;
    }, [registrations]);

    const displayData = useMemo(() => {
        return filteredWorks.map(work => {
            let count = work.interesteduser ? Object.keys(work.interesteduser).length : 0;
            if (count === 0) {
                count = workInterestMap[`${work.meetingNumber}_${work.workNumber}`] || 0;
            }
            return {
                ...work,
                interestedCount: count
            };
        });
    }, [filteredWorks, workInterestMap]);

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-10 px-2 text-left">
                    <div className="flex items-center gap-6">
                        <button 
                            onClick={() => navigate('/engineer/inter-governance-menu')}
                            className="p-4 bg-white rounded-2xl shadow-sm hover:bg-gray-50 transition-all text-gray-600 border border-gray-100 hover:scale-105 active:scale-95 group"
                        >
                            <FaArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <h1 className="text-2xl font-black text-gray-800 tracking-tight">जिल्हा इंटरेस्टेड कामांची यादी पहा</h1>
                    </div>
                    <div className="hidden md:flex items-center gap-3 text-[11px] font-black text-gray-400 bg-white px-6 py-3 rounded-2xl border border-gray-100 shadow-sm uppercase tracking-wider">
                        जिल्हा परिषद <FaChevronRight size={10} className="text-gray-200" /> <span className="text-teal-600">जिल्हा इंटरेस्टेड कामांची यादी पहा</span>
                    </div>
                </div>

                {/* Filter Section */}
                <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        <div className="space-y-2">
                            <label className="text-[13px] font-black text-gray-500 uppercase tracking-tight">विभाग</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-teal-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                            >
                                <option value="">विभाग निवड</option>
                                {departments.map((d, idx) => (
                                    <option key={idx} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[13px] font-black text-gray-500 uppercase tracking-tight">तालुका निवडा</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-teal-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={taluka}
                                onChange={(e) => setTaluka(e.target.value)}
                            >
                                <option value="">तालुका निवडा</option>
                                {talukas.map((t, idx) => (
                                    <option key={idx} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <button 
                                onClick={handleSearch}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/20 transition transform active:scale-95 flex items-center justify-center gap-2 w-full md:w-auto"
                            >
                                <FaSearch size={14} />
                                शोध
                            </button>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="mt-10 overflow-hidden border border-gray-100 rounded-[1.5rem] shadow-sm">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-white border-b border-gray-200 text-[13px] font-black text-gray-800 text-left uppercase">
                                    <th className="px-8 py-6 border-r border-gray-100 w-24">#</th>
                                    <th className="px-8 py-6 border-r border-gray-100 min-w-[180px]">कामाचा आयडी</th>
                                    <th className="px-8 py-6 border-r border-gray-100 min-w-[350px]">कामाचे नाव</th>
                                    <th className="px-8 py-6 border-r border-gray-100 min-w-[200px]">निवेदा रक्कम</th>
                                    <th className="px-8 py-6 min-w-[200px]">इंटरेस्टेड काउंट</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4 text-gray-300 font-bold">
                                                <div className="animate-spin text-blue-500 text-2xl">
                                                    <FaSearch size={24} />
                                                </div>
                                                <p className="italic">माहिती लोड होत आहे...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayData.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-8 py-10 text-center text-gray-500 font-bold bg-[#f8fafc]/50">
                                            यादी सापडली नाही.
                                        </td>
                                    </tr>
                                ) : (
                                    displayData.map((item, index) => (
                                        <tr key={index} className="hover:bg-blue-50/20 group transition-colors border-b border-gray-100 last:border-0 text-[14px] text-gray-700 font-bold">
                                            <td className="px-8 py-7 border-r border-gray-100 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                            <td className="px-8 py-7 border-r border-gray-100">{item.workNumber || "-"}</td>
                                            <td className="px-8 py-7 border-r border-gray-100 flex flex-col gap-1">
                                                <span className="font-marathi leading-relaxed">{item.workName}</span>
                                                <span className="text-[10px] text-gray-400 font-normal">{getMarathiTaluka(item.taluka)} | {item.schemeName}</span>
                                            </td>
                                            <td className="px-8 py-7 border-r border-gray-100 font-marathi">{item.tenderAmount || item.amount || "-"}</td>
                                            <td className="px-8 py-7">
                                                <div className="flex items-center gap-3">
                                                    <span className="font-marathi">{toMarathiNumber(item.interestedCount)}</span>
                                                    <span className="text-gray-300">-</span>
                                                    <button 
                                                        onClick={() => navigate(`/engineer/inter-governance-detail/${item.id}`)}
                                                        className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2 group/link"
                                                    >
                                                        <FaEye className="text-blue-400 group-hover/link:scale-110 transition-transform" />
                                                        <span className="font-marathi underline decoration-blue-200 underline-offset-4">
                                                            इंटरेस्टेड ॲप्लिकेशन पहा
                                                        </span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Footer Quote */}
                <div className="mt-12 text-center opacity-60">
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.4em]">
                        District Intelligence Framework • Inter-Governance Transparency
                    </p>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerInterGovernanceWorksList;
